import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, MapPin, Sun, Calendar as CalIcon, Heart, ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";
import ListingCard from "@/components/ListingCard";

export default function CityPage() {
  const { slug } = useParams();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    setLoading(true);
    api.get(`/cities/${slug}`).then(({ data }) => on && setCity(data)).catch(() => {}).finally(() => on && setLoading(false));
    return () => { on = false; };
  }, [slug]);

  if (loading) return <div className="pt-32 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!city) return <div className="pt-32 text-center text-neutral-500">Ville introuvable.</div>;

  return (
    <div className="pb-24" data-testid={`city-page-${city.slug}`}>
      {/* HERO */}
      <section className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <img src={city.hero} alt={city.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-16 text-white">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-bold text-[#C9986A] mb-3">
            <MapPin className="w-3.5 h-3.5" /> Bretagne · {city.region}
          </div>
          <h1 className="font-display text-5xl sm:text-7xl font-bold tracking-tight leading-[0.95]">{city.name}</h1>
          <p className="text-lg sm:text-xl text-white/85 mt-3 max-w-2xl italic">{city.tagline}</p>
        </div>
      </section>

      {/* INTRO + INFO */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 sm:mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#7A1535] mb-3">Bienvenue à {city.name}</div>
            <p className="text-lg leading-relaxed text-neutral-800">{city.intro}</p>

            {city.best_for?.length > 0 && (
              <div className="mt-8">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-3">Parfait pour</div>
                <div className="flex flex-wrap gap-2">
                  {city.best_for.map((b) => (
                    <span key={b} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-black/15 text-xs font-semibold">
                      <Sparkles className="w-3 h-3 text-[#7A1535]" /> {b}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <aside className="bg-[#F5F2EC] p-5 border border-black/10">
            <div className="mb-4">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">
                <Sun className="w-3 h-3" /> Climat
              </div>
              <p className="text-sm text-neutral-800">{city.weather}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">
                <CalIcon className="w-3 h-3" /> Meilleure saison
              </div>
              <p className="text-sm text-neutral-800">{city.best_season}</p>
            </div>
          </aside>
        </div>
      </section>

      {/* TIPS */}
      {city.tips?.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-8">
            Les conseils <em className="italic font-light">d'un local</em>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {city.tips.map((t, i) => (
              <div key={i} className="flex gap-4 p-5 bg-white border border-black/10">
                <div className="w-8 h-8 bg-[#0A0A0A] text-white font-display font-bold flex items-center justify-center shrink-0">{i + 1}</div>
                <p className="text-sm text-neutral-800 leading-relaxed">{t}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* LISTINGS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="flex items-baseline justify-between mb-8 flex-wrap gap-3">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            Logements à <em className="italic font-light">{city.name}</em>
          </h2>
          <Link to={`/search?city=${city.name}`} className="text-sm font-semibold inline-flex items-center gap-1 hover:text-[#7A1535]" data-testid="city-see-all">
            Voir tous les logements <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {city.listings && city.listings.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
            {city.listings.map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        ) : (
          <div className="text-center py-16 border border-dashed border-black/15">
            <Heart className="w-10 h-10 mx-auto text-neutral-300 mb-3" />
            <p className="text-neutral-500">Aucun logement publié pour le moment.</p>
          </div>
        )}
      </section>
    </div>
  );
}
